import { useParams } from 'react-router-dom'
import { Printer } from '@phosphor-icons/react'
import { Banner, Button } from '@/ui'
import { errorMessage } from '../api/core'
import { useFarmers } from '../api/farmers'
import { useVillages } from '../api/reference'
import { ConsolePage } from '../components/Layout'

/**
 * Printed village roster for the kit (F4.2, paper day F4-B3.a): roster number, name, father's name
 * and phone last 4, in roster-number order. The operator finds a farmer on it on a paper day and
 * writes his roster number on the slip. Print from the browser (A4, portrait).
 */
export function RosterPrintPage() {
  const { groupId } = useParams()
  const villages = useVillages()
  const village = villages.data?.find((v) => v.id === groupId)
  const q = useFarmers({ groupId, page: 1, limit: 200 })
  const rows = [...(q.data?.rows ?? [])].sort((a, b) =>
    (a.farmerCode ?? '~').localeCompare(b.farmerCode ?? '~'),
  )
  const noCode = rows.filter((r) => !r.farmerCode).length
  const printedOn = new Date().toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  return (
    <ConsolePage
      back={{ to: `/c/farmers?groupId=${groupId ?? ''}`, label: 'Farmers' }}
      eyebrow="Village roster · print"
      title={village?.name ?? 'Village roster'}
      description="For the field kit. Print it again after adding or changing farmers."
      actions={
        <Button
          leadingIcon={<Printer />}
          onClick={() => window.print()}
          disabled={rows.length === 0}
        >
          Print
        </Button>
      }
    >
      {q.isError && <Banner tone="error">{errorMessage(q.error)}</Banner>}
      {noCode > 0 && (
        <Banner tone="warn" className="print:hidden">
          {noCode} farmer{noCode === 1 ? ' has' : 's have'} no roster number yet: give them one on
          the Farmers page before printing.
        </Banner>
      )}
      {(q.data?.total ?? 0) > 200 && (
        <Banner tone="warn">Only the first 200 farmers are on this page.</Banner>
      )}
      <div className="roster-print rounded-card border border-rule bg-surface p-4 print:border-0 print:p-0">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-[20px] font-semibold">{village?.name ?? ''} · गाँव की सूची</h2>
          <span className="font-mono text-[12px] text-muted">
            {rows.length} farmers · printed {printedOn}
          </span>
        </div>
        <table className="w-full border-collapse text-[14px]">
          <thead>
            <tr className="border-b-2 border-ink text-left">
              <th className="py-1.5 pr-3">Roster no. · सूची नं.</th>
              <th className="py-1.5 pr-3">Name · नाम</th>
              <th className="py-1.5 pr-3">Father · पिता</th>
              <th className="py-1.5">Phone · फ़ोन</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading ? (
              <tr>
                <td colSpan={4} className="py-4 text-muted">
                  Loading…
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="break-inside-avoid border-b border-rule">
                  <td className="py-1.5 pr-3 font-mono font-semibold">{r.farmerCode ?? '—'}</td>
                  <td className="py-1.5 pr-3">{r.name}</td>
                  <td className="py-1.5 pr-3">{r.careOf ?? ''}</td>
                  <td className="py-1.5 font-mono">
                    {r.contactNo ? `••••${r.contactNo.slice(-4)}` : ''}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </ConsolePage>
  )
}
